import { useValidator } from "@kaladel/react/Hooks";
import { FC, useState } from "react";
import { useApp } from "../AppContext";
import { StaticCurrency } from "../ApiClient/models";
import { classNames, Severity } from "@kaladel/react/Utilities";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { Modal, ModalSize, Header, SleekInput, Autocomplete, Button, Form } from "@kaladel/react/Components";
import { createPortal } from "react-dom";
import modalStyles from "./ModalStyles.module.scss";
import { useApiClient } from "Contexts/ApiClientContext";

interface InvestmentAddModalProps {
    onClose: (dirty: boolean, newAccountId?: string) => void;
}

export const InvestmentAddModal: FC<InvestmentAddModalProps> = (props) => {
    const { apiCall, api } = useApiClient();
    const { currencies } = useApp();
    const validator = useValidator();
    const lastStage = 3;

    const [stage, setStage] = useState<number>(1);

    const [name, setName] = useState<string>("");
    const [currencyId, setCurrencyId] = useState<string>("");
    const [currencyName, setCurrencyName] = useState<string>("");
    const [balance, setBalance] = useState<string>("");

    const handleBackClicked = () => {
        if (stage !== 1) {
            const nextStage = stage - 1;

            setStage(nextStage);
        }
        else {
            props.onClose(false);
        }
    };

    const handleNextClicked = () => {
        if (!validator.canSubmit()) return;

        if (stage !== lastStage) {
            const nextStage = stage + 1;

            setStage(nextStage);

            validator.reset();
        }
        else {
            saveChanges();
        }
    };

    const getCurrencyAutocompleteResults = async (value: string): Promise<StaticCurrency[]> => {
        if (!currencies?.length) return [];

        return currencies.filter(c => c.code.toLowerCase().includes(value.toLowerCase()) || c.friendlyName.toLowerCase().includes(value.toLowerCase()));
    };

    const handleCurrencySelected = (currency: StaticCurrency) => {
        setCurrencyId(currency.code);
        setCurrencyName(`${currency.code} - ${currency.friendlyName}`);
    };

    const saveChanges = async () => {
        if (!validator.canSubmit()) return;

        const response = await apiCall(() => api.investmentaccounts.post({
            name,
            currencyId,
            balance: parseFloat(balance)
        }));

        if (response.kind === "success") {
            props.onClose(true, response.value);
        }
    };

    return (
        createPortal(
            <Modal
                onClose={() => props.onClose(false)}
                size={ModalSize.Medium}
            >
                <Header level={2} title="Add Investment Account" />

                <Form>

                    {stage === 1 && (
                        <>
                            <div className={modalStyles.label}>Give your investment account a name</div>

                            <SleekInput
                                id="name"
                                label="Name"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                validator={validator}
                                validations={{ required: true }}
                                autoFocus={true}
                            />
                        </>
                    )}

                    {stage === 2 && (
                        <>
                            <div className={modalStyles.label}>What currency is the account in?</div>

                            <Autocomplete<StaticCurrency>
                                autocompleteOptionKey={c => c.code}
                                autocompleteDisplayOption={c => `${c.code} - ${c.friendlyName}`}
                                onAutocomplete={getCurrencyAutocompleteResults}
                                onAutocompleteSelected={handleCurrencySelected}
                                value={currencyName}
                                label="Currency"
                                id="currencyId"
                                validator={validator}
                                validations={{ required: true }}
                                autoFocus={true}
                            />
                        </>
                    )}

                    {stage === 3 && (
                        <>
                            <div className={modalStyles.label}>What is the current cash balance?</div>

                            <SleekInput
                                id="balance"
                                label="Balance"
                                value={balance}
                                onChange={e => setBalance(e.target.value)}
                                validator={validator}
                                validations={{ required: true, numeric: true }}
                                autoFocus={true}
                            />
                        </>
                    )}

                    <div className={classNames([modalStyles.buttons, modalStyles.right])}>
                        <Button
                            onClick={handleBackClicked}
                            icon={faChevronLeft}
                            iconSide="left"
                            severity={Severity.Tertiary}
                        >
                            {stage !== 1 ? "Back" : "Cancel"}
                        </Button>

                        <Button
                            onClick={handleNextClicked}
                            icon={faChevronRight}
                            iconSide="right"
                            type="submit"
                        >
                            {stage !== lastStage ? "Next" : "Finish"}
                        </Button>
                    </div>
                </Form>

            </Modal>,
            document.body
        )
    );
};